import React, { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useFlags } from "@/lib/flags";
import { track } from "@/lib/analytics";
import { StarRating } from "@/components/site/Stars";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Plus, Building2, BookOpen, GraduationCap, Map as MapIcon, LayoutGrid, X, HandHeart, ArrowDownWideNarrow, Wallet, Users, MapPin, ArrowRight, Star, BadgeCheck, PenLine, School, SlidersHorizontal } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TYPE_LABELS_SHORT, TYPE_OPTIONS, GRADE_OPTIONS } from "@/lib/taxonomy";
import { getSchoolUrl } from "@/lib/slug";
import { formatSppRange, formatFirstYear, SPP_BUDGET_BUCKETS } from "@/lib/fees";
import CompareButton from "@/components/site/CompareButton";
import WishlistButton from "@/components/site/WishlistButton";
import SchoolMap from "@/components/site/SchoolMap";
import MultiSelectCheckbox from "@/components/site/MultiSelectCheckbox";
import { RELIGION_OPTIONS, RELIGION_LABEL } from "@/lib/taxonomy";
import MoneyInput from "@/components/site/MoneyInput";

/** Card slot helpers ────────────────────────────────────────────────────────
 * Kept top-level so the card renderer stays declarative. All follow the brief:
 *  - meta = plain text, not chips (Wilayah · Tipe · Agama)
 *  - primary curriculum = first non-generic entry; Merdeka wins over K13 when both present
 *  - accreditation pill only for A/B/C
 */
const GENERIC_CURRICULA = new Set(["umum", "general"]);

function pickPrimaryCurriculum(list) {
  if (!Array.isArray(list) || list.length === 0) return { primary: null, extra: 0 };
  const cleaned = list.filter((c) => c && !GENERIC_CURRICULA.has(String(c).toLowerCase()));
  if (cleaned.length === 0) return { primary: null, extra: 0 };
  // Merdeka+K13 dedupe: prefer Merdeka
  const hasMerdeka = cleaned.some((c) => /merdeka/i.test(c));
  const hasK13 = cleaned.some((c) => /^k[- ]?13$|kurikulum 2013/i.test(c));
  let primary = cleaned[0];
  if (hasMerdeka) primary = cleaned.find((c) => /merdeka/i.test(c));
  // Extra count excludes the primary; if Merdeka+K13 both present, count K13 as folded into primary
  let extra = cleaned.length - 1;
  if (hasMerdeka && hasK13) extra -= 1;
  return { primary, extra: Math.max(0, extra) };
}

function buildMetaLine(s) {
  const parts = [];
  if (s.city) parts.push(s.city);
  const t = TYPE_LABELS_SHORT[s.school_type] || s.school_type;
  if (t) parts.push(t);
  const rel = s.religion_affiliation;
  if (rel && String(rel).toLowerCase() !== "umum") {
    parts.push(RELIGION_LABEL[rel] || rel);
  }
  return parts.join(" · ");
}

export default function Schools() {
  const { school_reviews_enabled: reviewsOn } = useFlags();
  const [items, setItems] = useState([]);
  // Track whether we've completed the initial fetch — empty-state must NEVER
  // render before we know the true result. Also flips to true briefly on every
  // subsequent search/filter refetch so users see a skeleton instead of a
  // ghost "0 sekolah ditemukan" flash. See Bug #2 fix (Feb 2026).
  const [itemsLoading, setItemsLoading] = useState(true);
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  const [cities, setCities] = useState([]);
  const [curricula, setCurricula] = useState([]);
  const [view, setView] = useState("grid"); // grid | map
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();

  // Exploration mode: "jenjang" (individual school units, default) or "grup" (school groups).
  // Precedence: URL param > localStorage > default "jenjang". Persist selection so the choice
  // sticks across sessions (per brief spec).
  const initialMode = (() => {
    const urlMode = params.get("mode");
    if (urlMode === "jenjang" || urlMode === "grup") return urlMode;
    if (typeof window !== "undefined") {
      const stored = window.localStorage.getItem("tsr-explore-mode");
      if (stored === "jenjang" || stored === "grup") return stored;
    }
    return "jenjang";
  })();
  const [mode, setMode] = useState(initialMode);
  const [groups, setGroups] = useState([]);

  const [q, setQ] = useState(params.get("q") || "");
  // Multi-select filters (arrays)
  const parseCsv = (k) => { const v = params.get(k); return v ? v.split(",").filter(Boolean) : []; };
  const [cityMulti, setCityMulti] = useState(parseCsv("city"));
  const [curriculumMulti, setCurriculumMulti] = useState(parseCsv("curriculum"));
  const [gradeMulti, setGradeMulti] = useState(parseCsv("grade"));
  const [religionMulti, setReligionMulti] = useState(parseCsv("religion"));
  // Budget filters — only activated once user picks at least one Jenjang (per product spec).
  const [budgetBucket, setBudgetBucket] = useState(params.get("budget") || ""); // "0-2" | "2-5" | ... | "custom" | ""
  const [budgetCustomMax, setBudgetCustomMax] = useState(params.get("budget_custom") ? Number(params.get("budget_custom")) : "");
  const [budgetEntryMax, setBudgetEntryMax] = useState(params.get("budget_entry") ? Number(params.get("budget_entry")) : "");
  const [budgetFirstYearMax, setBudgetFirstYearMax] = useState(params.get("budget_first_year") ? Number(params.get("budget_first_year")) : "");
  const [budgetStrict, setBudgetStrict] = useState(params.get("budget_strict") === "1");
  // Anggaran Biaya box visibility — toggled by the "Biaya" pill next to Jenjang.
  // Default open when the user landed with any budget param in the URL so filters are visible.
  const [showBudget, setShowBudget] = useState(() =>
    !!(params.get("budget") || params.get("budget_custom") || params.get("budget_entry") || params.get("budget_first_year") || params.get("budget_strict"))
  );
  const [sortBy, setSortBy] = useState(params.get("sort") || "recent");

  const jenjangPicked = gradeMulti.length > 0;
  // Resolve the effective SPP cap from either a bucket preset or the "Atur Sendiri" custom input.
  const effectiveBudgetSppMax = React.useMemo(() => {
    if (!jenjangPicked || !budgetBucket) return null;
    if (budgetBucket === "custom") return budgetCustomMax ? Number(budgetCustomMax) : null;
    const b = SPP_BUDGET_BUCKETS.find((x) => x.value === budgetBucket);
    return b ? b.max : null;
  }, [jenjangPicked, budgetBucket, budgetCustomMax]);

  const fetch = () => {
    const p = {};
    if (q) p.q = q;
    if (cityMulti.length) p.city = cityMulti.join(",");
    if (curriculumMulti.length) p.curriculum = curriculumMulti.join(",");
    if (gradeMulti.length) p.grade = gradeMulti.join(",");
    if (religionMulti.length) p.religion = religionMulti.join(",");
    // Only send budget params when Jenjang is picked (product-spec hierarchy).
    if (jenjangPicked && effectiveBudgetSppMax) p.budget_spp_max = effectiveBudgetSppMax;
    if (jenjangPicked && budgetEntryMax) p.budget_entry_max = Number(budgetEntryMax);
    if (jenjangPicked && budgetFirstYearMax) p.budget_first_year_max = Number(budgetFirstYearMax);
    if (jenjangPicked && budgetStrict) p.budget_strict = true;
    if (sortBy && sortBy !== "recent") p.sort = sortBy;
    setItemsLoading(true);
    api.get("/schools", { params: p })
      .then((r) => {
        setItems(r.data);
        // search_school — spec section 5 rewrites this to strip the raw
        // query. Only structured, controlled values are shipped now. Raw
        // search text may contain names, phone numbers, or other user PII;
        // sending `has_query: true` preserves the funnel signal without ever
        // touching the string.
        const trimmed = (q || "").trim();
        if (!trimmed) return;
        track("search_school", {
          has_query: true,
          result_count: Array.isArray(r.data) ? r.data.length : 0,
          has_results: Array.isArray(r.data) && r.data.length > 0,
          filter_city: cityMulti.length ? cityMulti.join(",") : "",
          filter_curriculum: curriculumMulti.length ? curriculumMulti.join(",") : "",
          filter_grade: gradeMulti.length ? gradeMulti.join(",") : "",
          filter_religion: religionMulti.length ? religionMulti.join(",") : "",
          filter_budget_bucket: jenjangPicked ? (budgetBucket || "") : "",
          sort: sortBy || "recent",
        });
      })
      .catch(() => setItems([]))
      .finally(() => setItemsLoading(false));
  };

  useEffect(() => {
    api.get("/schools/meta/cities").then((r) => setCities(r.data)).catch(() => {});
    api.get("/schools/meta/curricula").then((r) => setCurricula(r.data)).catch(() => {});
    fetch();
    /* eslint-disable-next-line */
  }, []);

  // Fetch groups (lazily on first entry to grup mode; cache for the rest of the session).
  useEffect(() => {
    if (mode !== "grup" || groups.length > 0) return;
    api.get("/brands").then((r) => setGroups(r.data)).catch(() => setGroups([]));
    /* eslint-disable-next-line */
  }, [mode]);

  // Persist mode selection so it survives full-page reloads.
  useEffect(() => {
    if (typeof window !== "undefined") window.localStorage.setItem("tsr-explore-mode", mode);
  }, [mode]);

  useEffect(() => {
    const p = {};
    if (mode !== "jenjang") p.mode = mode;
    if (q) p.q = q;
    if (cityMulti.length) p.city = cityMulti.join(",");
    if (curriculumMulti.length) p.curriculum = curriculumMulti.join(",");
    if (gradeMulti.length) p.grade = gradeMulti.join(",");
    if (religionMulti.length) p.religion = religionMulti.join(",");
    if (sortBy && sortBy !== "recent") p.sort = sortBy;
    if (jenjangPicked && budgetBucket) p.budget = budgetBucket;
    if (jenjangPicked && budgetBucket === "custom" && budgetCustomMax) p.budget_custom = String(budgetCustomMax);
    if (jenjangPicked && budgetEntryMax) p.budget_entry = String(budgetEntryMax);
    if (jenjangPicked && budgetFirstYearMax) p.budget_first_year = String(budgetFirstYearMax);
    if (jenjangPicked && budgetStrict) p.budget_strict = "1";
    setParams(p);
    if (mode === "jenjang") fetch();
    /* eslint-disable-next-line */
  }, [mode, cityMulti, curriculumMulti, gradeMulti, religionMulti, budgetBucket, budgetCustomMax, budgetEntryMax, budgetFirstYearMax, budgetStrict]);

  const onSearch = (e) => { e.preventDefault(); fetch(); };

  // Search-as-you-type — debounce `q` changes and re-fetch. 350ms feels
  // responsive without spamming the API on every keystroke. Skipped in `grup`
  // mode since group filtering is client-side.
  useEffect(() => {
    if (mode !== "jenjang") return;
    const t = setTimeout(() => { fetch(); }, 350);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [q]);

  // Client-side sort — for "recent"/"rating"/"reviews"/"name" the backend
  // already returns items in the correct order (`GET /schools?sort=...`).
  // Only "fee_asc" and "newest" (added-to-directory date) still sort in
  // the client because they operate on fields the backend index does not
  // yet cover. React.useMemo below keeps rendering deterministic across
  // rehydrations (a reload always yields the same order for a given sort).
  const sortedItems = React.useMemo(() => {
    const arr = [...items];
    if (sortBy === "fee_asc") {
      arr.sort((a, b) => {
        const av = a.spp_min ?? a.monthly_spp_amount ?? a.tuition_monthly ?? Number.POSITIVE_INFINITY;
        const bv = b.spp_min ?? b.monthly_spp_amount ?? b.tuition_monthly ?? Number.POSITIVE_INFINITY;
        return av - bv;
      });
    } else if (sortBy === "newest") {
      arr.sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime());
    }
    // "recent" (default), "rating", "reviews", "name" → server-sorted; pass through.
    return arr;
  }, [items, sortBy]);

  const clearFilters = () => {
    setQ(""); setCityMulti([]); setCurriculumMulti([]); setGradeMulti([]); setReligionMulti([]);
    setBudgetBucket(""); setBudgetCustomMax(""); setBudgetEntryMax(""); setBudgetFirstYearMax(""); setBudgetStrict(false);
    setShowBudget(false);
    setParams({});
    setTimeout(fetch, 0);
  };

  const totalFilters = cityMulti.length + curriculumMulti.length + gradeMulti.length + religionMulti.length;
  const hasFilters = q || totalFilters > 0;

  // Group-mode: filter groups client-side by the search query. City/curriculum filters
  // don't apply to groups (they roll up multiple campuses across cities).
  const filteredGroups = React.useMemo(() => {
    if (!q) return groups;
    const needle = q.toLowerCase();
    return groups.filter((g) => (g.sample_name || "").toLowerCase().includes(needle));
  }, [groups, q]);

  return (
    <div data-testid="schools-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pt-4 sm:pt-14 lg:pt-20 pb-16">
      {/* ── Hero — compact on mobile so search bar + first card fit above the fold ─── */}
      <div className="mb-4 sm:mb-8 lg:mb-10">
        <div className="max-w-4xl">
          <div className="inline-flex items-center gap-2 rounded-full bg-white border border-[#EAE7E0] px-3 py-1 text-[10px] uppercase tracking-widest text-[#BE5730] font-semibold shadow-sm">
            <span className="w-1 h-1 rounded-full bg-[#D2673E]" aria-hidden="true" />
            Direktori Sekolah
          </div>
          <h1 className="font-serif-display text-2xl sm:text-4xl lg:text-6xl leading-[1.15] sm:leading-[1.1] tracking-tight text-[#1F2937] mt-2 sm:mt-4 line-clamp-2 sm:line-clamp-none">
            Temukan sekolah di seluruh Jabodetabek
          </h1>
        </div>
      </div>

      <form onSubmit={onSearch} data-testid="school-search-bar" className="mb-6">
        {/* Search + filters wrapped in a soft gray card, per mockup */}
        <div className="rounded-2xl sm:rounded-3xl bg-[#F3F1ED] border border-[#EAE7E0] p-2 sm:p-5">
          {/* Row 1: Rounded-full search input. Search-as-you-type — no
              separate submit button (per Feb 2026 mobile spec). Kept a
              minimal `onSubmit` on the <form> so Enter still triggers an
              immediate fetch instead of waiting for the debounce. */}
          <div className="flex items-stretch">
            <div className="flex-1 flex items-center gap-2 rounded-full bg-white border border-[#EAE7E0] pl-4 sm:pl-5 pr-3 h-12 sm:h-14 shadow-[0_2px_8px_-3px_rgba(31,41,55,0.06)]">
              <Search size={18} className="flex-shrink-0 text-[#8A8F99] pointer-events-none" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Cari nama sekolah"
                data-testid="school-search-input"
                className="flex-1 min-w-0 border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent text-base sm:text-lg placeholder-[#8A8F99] h-full px-1"
              />
              {q && (
                <button
                  type="button"
                  onClick={() => setQ("")}
                  data-testid="school-search-clear"
                  aria-label="Hapus pencarian"
                  className="flex-shrink-0 w-7 h-7 rounded-full text-[#8A8F99] hover:text-[#1F2937] hover:bg-[#F3F1ED] flex items-center justify-center transition-colors"
                >
                  ×
                </button>
              )}
            </div>
          </div>

          {/* Row 1 mobile: chip jenjang geser horizontal + ⚙ Filter button.
              Feb 2026 spec — replaces the multi-select dropdown row on mobile. */}
          {mode === "jenjang" && (
          <div className="mt-3 sm:hidden flex items-center gap-2" data-testid="mobile-jenjang-bar">
            <div className="flex-1 min-w-0 flex items-center gap-1.5 overflow-x-auto no-scrollbar">
              {(() => {
                const chips = [
                  { key: "__all", label: "Semua jenjang" },
                  { key: "tk", label: "TK" },
                  { key: "sd", label: "SD" },
                  { key: "smp", label: "SMP" },
                  { key: "sma", label: "SMA" },
                ];
                return chips.map((c) => {
                  const isAll = c.key === "__all";
                  const active = isAll ? gradeMulti.length === 0 : gradeMulti.includes(c.key);
                  return (
                    <button
                      key={c.key}
                      type="button"
                      onClick={() => {
                        if (isAll) { setGradeMulti([]); return; }
                        setGradeMulti(active
                          ? gradeMulti.filter((x) => x !== c.key)
                          : [...gradeMulti, c.key]);
                      }}
                      data-testid={`mobile-jenjang-${c.key.replace("__", "")}`}
                      className={
                        "shrink-0 px-3 h-9 rounded-full text-sm border transition-colors whitespace-nowrap " +
                        (active
                          ? "bg-[#1E3A8A] text-white border-[#1E3A8A]"
                          : "bg-white text-[#4B5563] border-[#EAE7E0] hover:bg-white")
                      }
                    >
                      {c.label}
                    </button>
                  );
                });
              })()}
            </div>
            {(() => {
              const advCount = cityMulti.length + religionMulti.length + curriculumMulti.length + (budgetBucket || budgetEntryMax || budgetFirstYearMax || budgetStrict ? 1 : 0);
              return (
                <button
                  type="button"
                  onClick={() => setFilterSheetOpen(true)}
                  data-testid="mobile-filter-open"
                  aria-label="Buka filter lanjutan"
                  className={
                    "shrink-0 inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-sm border transition-colors relative " +
                    (advCount > 0 ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "bg-white text-[#1F2937] border-[#EAE7E0]")
                  }
                >
                  <SlidersHorizontal size={14} strokeWidth={2.25} />
                  <span className="font-medium">Filter</span>
                  {advCount > 0 && (
                    <span className="ml-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-white text-[#1E3A8A] text-[10px] font-semibold tabular-nums flex items-center justify-center" data-testid="mobile-filter-count">{advCount}</span>
                  )}
                </button>
              );
            })()}
          </div>
          )}

          {/* Filter bottom sheet (mobile) — hosts every filter that used to sit
              in Row 3 (Wilayah, Agama, Kurikulum) plus the "Biaya" toggle. */}
          <Sheet open={filterSheetOpen} onOpenChange={setFilterSheetOpen}>
            <SheetContent side="bottom" className="rounded-t-2xl max-h-[85vh] overflow-y-auto sm:hidden" data-testid="schools-filter-sheet">
              <SheetHeader>
                <SheetTitle className="text-left font-serif-display text-xl text-[#1F2937]">Filter lanjutan</SheetTitle>
              </SheetHeader>
              <div className="mt-4 space-y-5">
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-[#4B5563] font-semibold mb-2">Wilayah</div>
                  <MultiSelectCheckbox
                    icon={<Building2 size={16} strokeWidth={1.75} className="text-[#1E3A8A]" />}
                    placeholder="Pilih kota / kecamatan"
                    options={cities}
                    value={cityMulti}
                    onChange={setCityMulti}
                    testid="sheet-filter-city"
                    maxWidthClass="w-full"
                  />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-[#4B5563] font-semibold mb-2">Agama</div>
                  <MultiSelectCheckbox
                    icon={<HandHeart size={16} strokeWidth={1.75} className="text-[#1E3A8A]" />}
                    placeholder="Pilih agama"
                    options={RELIGION_OPTIONS}
                    value={religionMulti}
                    onChange={setReligionMulti}
                    testid="sheet-filter-religion"
                    maxWidthClass="w-full"
                  />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-[#4B5563] font-semibold mb-2">Kurikulum / Tipe</div>
                  <MultiSelectCheckbox
                    icon={<BookOpen size={16} strokeWidth={1.75} className="text-[#1E3A8A]" />}
                    placeholder="Pilih kurikulum"
                    options={curricula}
                    value={curriculumMulti}
                    onChange={setCurriculumMulti}
                    testid="sheet-filter-curriculum"
                    maxWidthClass="w-full"
                  />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-[#4B5563] font-semibold mb-2">Rentang biaya SPP / bulan</div>
                  <div className="flex flex-wrap gap-2">
                    {SPP_BUDGET_BUCKETS.map((b) => (
                      <button
                        key={b.value}
                        type="button"
                        onClick={() => setBudgetBucket(budgetBucket === b.value ? "" : b.value)}
                        data-testid={`sheet-budget-bucket-${b.value}`}
                        disabled={!jenjangPicked}
                        className={
                          "px-3 py-1.5 rounded-full text-xs border transition-colors " +
                          (budgetBucket === b.value
                            ? "bg-[#92400E] text-white border-[#92400E]"
                            : "bg-white text-[#4B5563] border-[#EAE7E0] hover:bg-[#FEF3C7]") +
                          (!jenjangPicked ? " opacity-50 cursor-not-allowed" : "")
                        }
                      >
                        {b.label}
                      </button>
                    ))}
                  </div>
                  {!jenjangPicked && (
                    <div className="mt-1.5 text-[11px] text-[#8A8F99]">Pilih jenjang di baris atas untuk mengaktifkan filter biaya.</div>
                  )}
                </div>
                <div className="pt-2 flex items-center justify-between gap-2 border-t border-[#EAE7E0]">
                  <button
                    type="button"
                    onClick={() => { clearFilters(); setFilterSheetOpen(false); }}
                    data-testid="sheet-filter-reset"
                    className="text-sm text-[#4B5563] hover:text-[#B91C1C] transition-colors"
                  >
                    Reset filter
                  </button>
                  <Button
                    type="button"
                    onClick={() => setFilterSheetOpen(false)}
                    data-testid="sheet-filter-apply"
                    className="rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white h-10 px-6"
                  >
                    Tampilkan hasil
                  </Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>



          {/* Row 2: Quick jenjang shortcuts — icon-led navigation to TK / SD / SMP / SMA.
              Desktop only; mobile has its own dedicated Row 1 chip bar + ⚙ Filter
              bottom sheet below. */}
          {mode === "jenjang" && (
            <div className="mt-4 hidden sm:flex flex-wrap items-center gap-2" data-testid="jenjang-quick-nav">
              <span className="text-[10px] uppercase tracking-widest text-[#4B5563] font-semibold mr-1">Jenjang</span>
              {[
                { key: "tk", label: "TK", emoji: "🧸" },
                { key: "sd", label: "SD", emoji: "✏️" },
                { key: "smp", label: "SMP", emoji: "📘" },
                { key: "sma", label: "SMA", emoji: "🎓" },
              ].map((g) => {
                const active = gradeMulti.includes(g.key);
                return (
                  <button
                    key={g.key}
                    type="button"
                    onClick={() => {
                      setGradeMulti(active
                        ? gradeMulti.filter((x) => x !== g.key)
                        : [...gradeMulti, g.key]);
                    }}
                    data-testid={`jenjang-quick-${g.key}`}
                    className={
                      "inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm border transition-colors " +
                      (active
                        ? "bg-[#1E3A8A] text-white border-[#1E3A8A]"
                        : "bg-white text-[#1F2937] border-[#E8E4DB] hover:border-[#1E3A8A] hover:bg-white")
                    }
                  >
                    <span aria-hidden="true">{g.emoji}</span>
                    <span className="font-medium">{g.label}</span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Row 3: Filter dropdowns row (each with left icon + label + chevron via MultiSelectCheckbox).
              Desktop-only; mobile filters are exposed via the ⚙ Filter bottom sheet. */}
          {mode === "jenjang" && (
          <div className="mt-3 hidden sm:flex flex-nowrap sm:flex-wrap items-center gap-2 overflow-x-auto no-scrollbar" style={{ WebkitOverflowScrolling: "touch" }}>
          <MultiSelectCheckbox
            icon={<Building2 size={16} strokeWidth={1.75} className="text-[#1E3A8A]" />}
            placeholder="Wilayah"
            options={cities}
            value={cityMulti}
            onChange={setCityMulti}
            testid="school-filter-city"
            maxWidthClass="flex-shrink-0 min-w-[160px]"
          />
          <MultiSelectCheckbox
            icon={<HandHeart size={16} strokeWidth={1.75} className="text-[#1E3A8A]" />}
            placeholder="Agama"
            options={RELIGION_OPTIONS}
            value={religionMulti}
            onChange={setReligionMulti}
            testid="school-filter-religion"
            maxWidthClass="flex-shrink-0 min-w-[150px]"
          />
          <MultiSelectCheckbox
            icon={<BookOpen size={16} strokeWidth={1.75} className="text-[#1E3A8A]" />}
            placeholder="Kurikulum"
            options={curricula}
            value={curriculumMulti}
            onChange={setCurriculumMulti}
            testid="school-filter-curriculum"
            maxWidthClass="flex-shrink-0 min-w-[170px]"
          />
          <MultiSelectCheckbox
            icon={<GraduationCap size={16} strokeWidth={1.75} className="text-[#1E3A8A]" />}
            placeholder="Jenjang"
            options={GRADE_OPTIONS.map((g) => ({ value: g.value, label: g.label }))}
            value={gradeMulti}
            onChange={setGradeMulti}
            testid="school-filter-grade"
            maxWidthClass="flex-shrink-0 min-w-[150px]"
          />
          {/* "Biaya" toggle — shows/hides the Anggaran Biaya box. Kept next to Jenjang because the
              budget filter depends on Jenjang being picked (product-spec hierarchy).
              Styled to match the sibling MultiSelectCheckbox pills exactly (h-12, same border/font/hover). */}
          {(() => {
            const budgetActive = !!(budgetBucket || budgetEntryMax || budgetFirstYearMax || budgetStrict);
            const activeState = showBudget || budgetActive;
            return (
              <button
                type="button"
                onClick={() => setShowBudget((v) => !v)}
                data-testid="school-filter-biaya-toggle"
                aria-expanded={showBudget}
                aria-pressed={activeState}
                className={
                  "flex-shrink-0 h-12 rounded-full flex items-center gap-2 px-4 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D2673E]/40 " +
                  (activeState
                    ? "bg-white border border-[#D2673E] text-[#1F2937] font-medium"
                    : "bg-white border border-[#E8E4DB] text-[#1F2937] hover:border-[#D2673E] hover:bg-[#FEFAF7]")
                }
              >
                <Wallet size={16} strokeWidth={1.75} className="shrink-0 text-[#1E3A8A]" />
                <span>Biaya</span>
                {budgetActive && (
                  <span
                    className="shrink-0 w-5 h-5 rounded-full bg-[#F6E7DF] text-[#BE5730] text-[11px] font-semibold flex items-center justify-center"
                    data-testid="biaya-active-badge"
                  >
                    {[budgetBucket, budgetEntryMax, budgetFirstYearMax].filter(Boolean).length + (budgetStrict ? 1 : 0)}
                  </span>
                )}
              </button>
            );
          })()}
        </div>
        )}

        {/* Row 2.5: Budget filters — hidden by default; toggled via the "Biaya" pill above.
            Rendering is conditional so mobile no longer has a permanent scroll blocker. */}
        {mode === "jenjang" && showBudget && (
        <div className="mt-3 rounded-2xl bg-white border border-[#FDE68A] p-3" data-testid="budget-filters-block">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="text-[11px] uppercase tracking-widest text-[#92400E] font-semibold">
              Anggaran Biaya
              {!jenjangPicked && (
                <span className="ml-2 normal-case font-normal text-[#4B5563]">— pilih <em>Jenjang</em> dulu untuk mengaktifkan filter ini</span>
              )}
            </div>
            {jenjangPicked && (budgetBucket || budgetEntryMax || budgetFirstYearMax || budgetStrict) ? (
              <button type="button" onClick={() => { setBudgetBucket(""); setBudgetCustomMax(""); setBudgetEntryMax(""); setBudgetFirstYearMax(""); setBudgetStrict(false); }}
                className="text-[10px] uppercase tracking-widest text-[#BE5730] hover:underline" data-testid="budget-clear-btn">
                Reset anggaran
              </button>
            ) : null}
          </div>
          <fieldset disabled={!jenjangPicked} className={"mt-2 " + (jenjangPicked ? "" : "opacity-50 pointer-events-none")} data-testid="budget-filters-fieldset">
            {/* Primary: monthly SPP budget buckets */}
            <div className="flex flex-wrap gap-2">
              {SPP_BUDGET_BUCKETS.map((b) => (
                <button type="button" key={b.value}
                  onClick={() => { setBudgetBucket(b.value); setBudgetCustomMax(""); }}
                  data-testid={`budget-bucket-${b.value}`}
                  className={"px-3 py-1.5 rounded-full text-xs border transition-colors " + (budgetBucket === b.value ? "bg-[#92400E] text-white border-[#92400E]" : "bg-white text-[#4B5563] border-[#EAE7E0] hover:bg-[#FEF3C7]")}>
                  {b.label}
                </button>
              ))}
              <button type="button"
                onClick={() => setBudgetBucket("custom")}
                data-testid="budget-bucket-custom"
                className={"px-3 py-1.5 rounded-full text-xs border transition-colors " + (budgetBucket === "custom" ? "bg-[#92400E] text-white border-[#92400E]" : "bg-white text-[#4B5563] border-[#EAE7E0] hover:bg-[#FEF3C7]")}>
                Atur Sendiri
              </button>
            </div>
            {budgetBucket === "custom" && (
              <div className="mt-2 max-w-xs" data-testid="budget-custom-input-wrap">
                <MoneyInput placeholder="Anggaran SPP maksimal (Rp/bulan)"
                  value={budgetCustomMax}
                  onChange={(n) => setBudgetCustomMax(n ?? "")}
                  data-testid="budget-custom-input" />
              </div>
            )}
            {/* Supporting filters */}
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#4B5563] mb-1">Uang Pangkal Maksimal</div>
                <MoneyInput placeholder="mis: 50.000.000" value={budgetEntryMax}
                  onChange={(n) => setBudgetEntryMax(n ?? "")} data-testid="budget-entry-max" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#4B5563] mb-1">Estimasi Biaya Tahun Pertama Maksimal</div>
                <MoneyInput placeholder="mis: 120.000.000" value={budgetFirstYearMax}
                  onChange={(n) => setBudgetFirstYearMax(n ?? "")} data-testid="budget-first-year-max" />
              </div>
            </div>
            {/* Strict toggle */}
            <label className="mt-3 flex items-center gap-2 text-xs text-[#1F2937]" data-testid="budget-strict-wrap">
              <input type="checkbox" checked={budgetStrict} onChange={(e) => setBudgetStrict(e.target.checked)} data-testid="budget-strict-checkbox" className="w-3.5 h-3.5 accent-[#92400E]" />
              Hanya tampilkan sekolah yang <strong>sepenuhnya</strong> masuk anggaran <span className="text-[#4B5563]">(Nilai Maksimum ≤ Anggaran)</span>
            </label>
          </fieldset>
        </div>
        )}

        {mode === "jenjang" && totalFilters > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-1.5" data-testid="active-filter-pills">
            {cityMulti.map((v) => (
              <button key={"c" + v} type="button" onClick={() => setCityMulti(cityMulti.filter((x) => x !== v))}
                className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#EEF2FF] text-[#1E3A8A] border border-[#DBEAFE] text-xs hover:bg-[#DBEAFE] transition-colors"
                data-testid={`active-pill-city-${v}`}>
                {v} <X size={10} strokeWidth={2.5} />
              </button>
            ))}
            {religionMulti.map((v) => (
              <button key={"r" + v} type="button" onClick={() => setReligionMulti(religionMulti.filter((x) => x !== v))}
                className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#FDF6EC] text-[#92400E] border border-[#FDE68A] text-xs hover:bg-[#FEF3C7] transition-colors"
                data-testid={`active-pill-religion-${v}`}>
                {RELIGION_LABEL[v] || v} <X size={10} strokeWidth={2.5} />
              </button>
            ))}
            {curriculumMulti.map((v) => (
              <button key={"k" + v} type="button" onClick={() => setCurriculumMulti(curriculumMulti.filter((x) => x !== v))}
                className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#F3F1ED] text-[#4B5563] border border-[#EAE7E0] text-xs hover:bg-[#EAE7E0] transition-colors"
                data-testid={`active-pill-curriculum-${v}`}>
                {v} <X size={10} strokeWidth={2.5} />
              </button>
            ))}
            {gradeMulti.map((v) => (
              <button key={"g" + v} type="button" onClick={() => setGradeMulti(gradeMulti.filter((x) => x !== v))}
                className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#ECFDF5] text-[#065F46] border border-[#A7F3D0] text-xs hover:bg-[#D1FAE5] transition-colors"
                data-testid={`active-pill-grade-${v}`}>
                {(GRADE_OPTIONS.find((g) => g.value === v)?.label) || v} <X size={10} strokeWidth={2.5} />
              </button>
            ))}
            <button
              type="button"
              onClick={() => { setCityMulti([]); setCurriculumMulti([]); setGradeMulti([]); setReligionMulti([]); }}
              data-testid="school-filter-clear-all"
              className="ml-1 h-7 px-3 rounded-full text-[10px] uppercase tracking-widest font-semibold text-[#BE5730] hover:bg-[#F6E7DF] transition-colors inline-flex items-center gap-1"
            >
              <X size={10} strokeWidth={2.5} /> Hapus semua
            </button>
          </div>
        )}
        </div>{/* /gray filter card */}
      </form>

      {/* Counter row.
          Mobile (Feb 2026 spec): segmented mode toggle left · Sort right.
          The count "N sekolah ditemukan" moves to its own line below.
          Desktop keeps the compact inline layout (count in the same row). */}
      <div className="mb-2 sm:mb-5">
        <div className="flex items-center justify-between gap-2 sm:gap-3 flex-wrap">
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
            <div className="inline-flex items-center rounded-full bg-white border border-[#E8E4DB] p-1" data-testid="exploration-mode-toggle" role="tablist" aria-label="Mode eksplorasi">
              <button
                type="button"
                role="tab"
                aria-selected={mode === "jenjang"}
                onClick={() => setMode("jenjang")}
                data-testid="mode-jenjang"
                className={"inline-flex items-center px-3 sm:px-4 py-1.5 rounded-full text-[13px] sm:text-sm transition-colors " + (mode === "jenjang" ? "bg-[#1E3A8A] text-white font-medium shadow-sm" : "text-[#4B5563] hover:text-[#1F2937]")}
              >
                Per sekolah
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={mode === "grup"}
                onClick={() => setMode("grup")}
                data-testid="mode-grup"
                className={"inline-flex items-center px-3 sm:px-4 py-1.5 rounded-full text-[13px] sm:text-sm transition-colors " + (mode === "grup" ? "bg-[#1E3A8A] text-white font-medium shadow-sm" : "text-[#4B5563] hover:text-[#1F2937]")}
              >
                Per yayasan
              </button>
            </div>
            <span className="hidden sm:inline text-[#8A8F99] text-sm" aria-hidden="true">·</span>
            <div className="hidden sm:block text-sm text-[#4B5563]" data-testid="school-count">
              {mode === "jenjang" && itemsLoading ? (
                <span className="text-[#8A8F99]">Memuat sekolah…</span>
              ) : (
                <>
                  <span className="font-semibold text-[#1F2937]">{mode === "grup" ? filteredGroups.length : sortedItems.length}</span>{" "}
                  {mode === "grup" ? "grup sekolah ditemukan" : "sekolah ditemukan"}
                </>
              )}
            </div>
          </div>
          {mode === "jenjang" && (
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
            {/* Sort dropdown — compact on mobile */}
            <div className="inline-flex items-center gap-1.5 sm:gap-2" data-testid="sort-wrap">
              <ArrowDownWideNarrow size={14} className="hidden sm:block text-[#4B5563]" />
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[140px] sm:w-[200px] h-9 sm:h-10 rounded-full text-[13px] sm:text-sm bg-white border-[#EAE7E0]" data-testid="school-sort-select">
                  <SelectValue placeholder="Urutkan" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="recent" data-testid="sort-option-recent">{reviewsOn ? "Terbaru direview" : "Paling relevan"}</SelectItem>
                  {reviewsOn && <SelectItem value="rating" data-testid="sort-option-rating">Rating tertinggi</SelectItem>}
                  {reviewsOn && <SelectItem value="reviews" data-testid="sort-option-reviews">Review terbanyak</SelectItem>}
                  <SelectItem value="name" data-testid="sort-option-name">Nama A–Z</SelectItem>
                  <SelectItem value="fee_asc" data-testid="sort-option-fee-asc">Biaya terendah</SelectItem>
                  <SelectItem value="newest" data-testid="sort-option-newest">Terbaru ditambahkan</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {/* Grid/Peta toggle — desktop-only (mobile stays in list view) */}
            <div className="hidden sm:inline-flex items-center rounded-full bg-white border border-[#E8E4DB] p-1" data-testid="view-toggle">
              <button
                onClick={() => setView("grid")}
                data-testid="view-grid"
                className={"inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-medium transition-colors " + (view === "grid" ? "bg-[#1E3A8A] text-white shadow-sm" : "text-[#4B5563]")}
              >
                <LayoutGrid size={12} /> Grid
              </button>
              <button
                onClick={() => setView("map")}
                data-testid="view-map"
                className={"inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-medium transition-colors " + (view === "map" ? "bg-[#1E3A8A] text-white shadow-sm" : "text-[#4B5563]")}
              >
                <MapIcon size={12} /> Peta
              </button>
            </div>
          </div>
          )}
        </div>
        {/* Mobile-only count line beneath the row */}
        <div className="mt-2 sm:hidden text-sm text-[#4B5563]" data-testid="school-count-mobile">
          {mode === "jenjang" && itemsLoading ? (
            <span className="text-[#8A8F99]">Memuat sekolah…</span>
          ) : (
            <>
              <span className="font-semibold text-[#1F2937]">{mode === "grup" ? filteredGroups.length : sortedItems.length}</span>{" "}
              {mode === "grup" ? "grup sekolah ditemukan" : "sekolah ditemukan"}
            </>
          )}
        </div>
      </div>

      {mode === "grup" ? (
        filteredGroups.length === 0 ? (
          <div className="text-center py-16" data-testid="groups-empty">
            <div className="text-5xl mb-4" aria-hidden>🏫</div>
            <div className="font-serif-display text-2xl text-[#1F2937]">Belum ada grup sekolah yang cocok</div>
            <p className="mt-2 text-sm text-[#4B5563] max-w-md mx-auto">
              Grup sekolah adalah yayasan dengan minimal 2 cabang. Coba hapus kata kunci pencarian.
            </p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="groups-grid">
            {filteredGroups.map((b, i) => (
              <GroupExplorationCard key={`${b.kind}-${b.brand_id}`} b={b} i={i} />
            ))}
          </div>
        )
      ) : itemsLoading ? (
        // Skeleton placeholders — same rough footprint as the grid cards so
        // the layout doesn't shift when real data arrives. Empty-state is
        // intentionally NEVER rendered while a request is inflight.
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="schools-loading-skeleton">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="rounded-2xl border border-[#EAE7E0] bg-white overflow-hidden animate-pulse">
              <div className="h-40 bg-[#F3F1ED]" />
              <div className="p-4 space-y-3">
                <div className="h-4 w-3/4 bg-[#EAE7E0] rounded" />
                <div className="h-3 w-1/2 bg-[#F3F1ED] rounded" />
                <div className="h-3 w-2/3 bg-[#F3F1ED] rounded" />
                <div className="pt-3 flex gap-2">
                  <div className="h-6 w-16 bg-[#F3F1ED] rounded-full" />
                  <div className="h-6 w-20 bg-[#F3F1ED] rounded-full" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : sortedItems.length === 0 ? (
        <div className="text-center py-16 flex flex-col items-center gap-4" data-testid="schools-empty">
          <div className="text-5xl" aria-hidden>🔍</div>
          <div>
            <div className="font-serif-display text-2xl text-[#1F2937]">Tidak ada sekolah yang cocok</div>
            <p className="mt-2 text-sm text-[#4B5563] max-w-md">Coba longgarkan filter atau ubah kata kunci pencarian. Anda juga bisa membantu komunitas dengan mengusulkan sekolah baru.</p>
          </div>
          <Link to="/sekolah/usulkan">
            <Button data-testid="empty-submit-cta" className="rounded-full bg-[#D2673E] hover:bg-[#C25730] text-white h-11 px-6">
              <Plus size={16} className="mr-2" /> Sekolah belum terdaftar? Usulkan di sini.
            </Button>
          </Link>
        </div>
      ) : view === "map" ? (
        <SchoolMap schools={sortedItems} />
      ) : (
        <>
        {/* Mobile-only compact list — thumbnail LEFT + info RIGHT, whole card clickable */}
        <ul className="sm:hidden space-y-3" data-testid="schools-list-mobile">
          {sortedItems.map((s, i) => {
            const sppLine = formatSppRange(s.spp_min, s.spp_max);
            const { primary: primaryCurriculum } = pickPrimaryCurriculum(s.curriculum);
            const location = [s.kecamatan, s.city].filter(Boolean).join(", ");
            const typeLabel = s.school_type === "internasional" ? "Internasional" : s.school_type === "nasional-plus" ? "Nasional Plus" : s.school_type === "negeri" ? "Negeri" : s.school_type === "swasta" ? "Swasta" : null;
            const tags = [typeLabel, primaryCurriculum].filter(Boolean).slice(0, 3);
            const hasReviews = (s.review_count || 0) > 0;
            return (
              <li
                key={s.school_id}
                data-testid={`school-card-mobile-${i}`}
                onClick={() => nav(getSchoolUrl(s))}
                role="link"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); nav(getSchoolUrl(s)); } }}
                className="bg-white border border-[#EAE7E0] rounded-2xl overflow-hidden flex gap-3 p-3 cursor-pointer hover:shadow-sm transition-shadow"
              >
                {/* Thumbnail — 96×96 square, rounded */}
                <div className="w-24 h-24 flex-shrink-0 rounded-xl bg-[#F3F1ED] overflow-hidden">
                  {s.image_url ? (
                    <img src={s.image_url} alt={s.name} loading="lazy" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[#8A8F99]">
                      <School size={28} strokeWidth={1.5} />
                    </div>
                  )}
                </div>
                {/* Info block */}
                <div className="flex-1 min-w-0 flex flex-col justify-between">
                  <div className="min-w-0">
                    <div className="font-semibold text-[#1F2937] text-[15px] leading-snug line-clamp-2" data-testid={`school-card-mobile-${i}-name`}>{s.name}</div>
                    {/* Meta line: rating · reviews · price */}
                    <div className="mt-1 flex items-center gap-1.5 text-xs text-[#4B5563] flex-wrap" data-testid={`school-card-mobile-${i}-meta`}>
                      {reviewsOn && (hasReviews ? (
                        <span className="inline-flex items-center gap-1 text-[#1F2937]">
                          <Star size={11} className="text-[#E4B24C] fill-[#E4B24C]" />
                          <span className="tabular-nums font-medium">{Number(s.avg_rating || 0).toFixed(1)}</span>
                          <span className="text-[#4B5563]">· {s.review_count} review</span>
                        </span>
                      ) : (
                        <span className="text-[#8A8F99]">Belum ada review</span>
                      ))}
                      {sppLine && <span className="text-[#EAE7E0]">·</span>}
                      {sppLine && <span className="text-[#1F2937] font-medium">{sppLine}</span>}
                    </div>
                  </div>
                  {/* Location */}
                  {location && (
                    <div className="mt-1 inline-flex items-center gap-1 text-[11px] text-[#4B5563] min-w-0" data-testid={`school-card-mobile-${i}-location`}>
                      <MapPin size={11} className="flex-shrink-0 text-[#8A8F99]" />
                      <span className="truncate">{location}</span>
                    </div>
                  )}
                  {/* Tags — small coral outline chips */}
                  {tags.length > 0 && (
                    <div className="mt-1.5 flex items-center gap-1 flex-wrap" data-testid={`school-card-mobile-${i}-tags`}>
                      {tags.map((tag, idx) => (
                        <span key={idx} className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-[#FFF6F1] text-[#C25A3E] border border-[#F3C6B3]">{tag}</span>
                      ))}
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>

        {/* Desktop grid — untouched by Feb 2026 mobile redesign */}
        <div className="hidden sm:grid md:grid-cols-3 lg:grid-cols-4 gap-6">
          {sortedItems.map((s, i) => {
            const { primary: primaryCurriculum, extra: extraCurriculum } = pickPrimaryCurriculum(s.curriculum);
            const metaLine = buildMetaLine(s);
            const showAccreditation = ["A", "B", "C"].includes(s.accreditation_grade);
            const hasReviews = (s.review_count || 0) > 0;
            const sppLine = formatSppRange(s.spp_min, s.spp_max);
            const firstYear = formatFirstYear(s);
            return (
              <div
                key={s.school_id}
                data-testid={`school-card-${i}`}
                className="group bg-white border border-[#EAE7E0] rounded-2xl overflow-hidden hover:-translate-y-1 hover:shadow-[0_12px_32px_rgba(0,0,0,0.06)] transition-transform duration-300 animate-fade-up flex flex-col"
                style={{ animationDelay: `${i * 40}ms` }}
              >
                <Link to={getSchoolUrl(s)} className="block">
                  <div className="aspect-[4/3] bg-[#F3F1ED] overflow-hidden relative">
                    {s.image_url && (
                      <img src={s.image_url} alt={s.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    )}
                    {showAccreditation && (
                      <div className="absolute top-2 left-2" data-testid={`card-accreditation-${i}`}>
                        <span
                          className={
                            "inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-bold border bg-white/95 backdrop-blur-sm shadow-sm " +
                            (s.accreditation_grade === "A"
                              ? "text-[#065F46] border-[#A7F3D0]"
                              : s.accreditation_grade === "B"
                              ? "text-[#1E3A8A] border-[#DBEAFE]"
                              : "text-[#92400E] border-[#FDE68A]")
                          }
                        >
                          Akreditasi {s.accreditation_grade}
                        </span>
                      </div>
                    )}
                    <div className="absolute top-2 right-2">
                      <WishlistButton schoolId={s.school_id} />
                    </div>
                  </div>
                </Link>
                <div className="p-5 flex-1 flex flex-col">
                  {/* Slot 1 — Meta (plain text): Wilayah · Tipe · Agama */}
                  <div className="text-[11px] uppercase tracking-widest text-[#4B5563] font-medium" data-testid={`card-meta-${i}`}>
                    {metaLine}
                  </div>

                  {/* Slot 2 — Name with verified checkmark */}
                  <Link
                    to={getSchoolUrl(s)}
                    className="mt-1.5 font-serif-display text-lg text-[#1F2937] leading-tight hover:text-[#1E3A8A] inline-flex items-start gap-1.5"
                    data-testid={`card-name-${i}`}
                  >
                    <span>{s.name}</span>
                    {s.government_verified && (
                      <BadgeCheck
                        size={16}
                        strokeWidth={2.25}
                        className="flex-shrink-0 mt-1 text-[#1E3A8A]"
                        aria-label="Terverifikasi pemerintah"
                        data-testid={`card-verified-${i}`}
                      />
                    )}
                  </Link>

                  {/* Slot 3 — Curriculum: one primary chip + "+n". Fixed min height so cards stay uniform. */}
                  <div className="mt-2 min-h-[22px] flex items-center gap-1.5" data-testid={`card-curriculum-${i}`}>
                    {primaryCurriculum && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider bg-[#EEF2FF] text-[#1E3A8A] border border-[#DBEAFE]">
                        {primaryCurriculum}
                      </span>
                    )}
                    {extraCurriculum > 0 && (
                      <span className="text-[10px] text-[#4B5563] font-medium" data-testid={`card-curriculum-extra-${i}`}>
                        +{extraCurriculum}
                      </span>
                    )}
                  </div>

                  {/* Slot 4 — Cost */}
                  <div className="mt-3 text-sm" data-testid={`card-fee-${i}`}>
                    <div className="font-semibold text-[#1E3A8A]" data-testid={`card-spp-${i}`}>{sppLine}</div>
                    {firstYear && (
                      <div className="text-xs text-[#4B5563]" data-testid={`card-first-year-${i}`}>
                        Tahun pertama {firstYear}
                      </div>
                    )}
                    {s.budget_fit === "partial" && (
                      <div className="mt-1 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] bg-[#FEF3C7] text-[#92400E] border border-[#FDE68A]" data-testid={`card-budget-partial-${i}`}>
                        Sebagian opsi berada dalam anggaran Anda
                      </div>
                    )}
                  </div>

                  {/* Slot 5 — Rating (or empty state CTA) */}
                  {reviewsOn && <div className="mt-auto pt-4" data-testid={`card-rating-${i}`}>
                    {hasReviews ? (
                      <div className="flex items-center gap-2">
                        <StarRating value={s.avg_rating} testid={`school-rating-${i}`} />
                        <span className="text-sm font-semibold text-[#1F2937] tabular-nums">
                          {(s.avg_rating || 0).toFixed(1)}
                        </span>
                        <span className="text-xs text-[#4B5563]">· {s.review_count} review</span>
                      </div>
                    ) : (
                      <Link
                        to={getSchoolUrl(s) + "#write-review"}
                        className="inline-flex items-center gap-1.5 text-xs text-[#BE5730] font-semibold hover:text-[#D2673E] transition-colors"
                        data-testid={`card-empty-review-cta-${i}`}
                      >
                        <PenLine size={12} strokeWidth={2.25} /> Jadi reviewer pertama
                      </Link>
                    )}
                  </div>}

                  <div className="mt-3 pt-3 border-t border-[#EAE7E0]">
                    <CompareButton schoolId={s.school_id} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        {/* Secondary CTA — "Belum menemukan sekolahmu? + Usulkan sekolah".
            Shown on all viewports below the list; replaces the header
            Usulkan pill on mobile. */}
        <div className="mt-8 sm:mt-10 pt-6 border-t border-[#EAE7E0] text-center" data-testid="schools-usulkan-secondary">
          <span className="text-sm text-[#4B5563]">Belum menemukan sekolahmu? </span>
          <Link to="/sekolah/usulkan" className="inline-flex items-center gap-1 text-sm font-semibold text-[#D2673E] hover:text-[#C25730] transition-colors" data-testid="schools-usulkan-secondary-link">
            <Plus size={14} strokeWidth={2.5} /> Usulkan sekolah
          </Link>
        </div>
        </>
      )}
    </div>
  );
}

/** Group card rendered in `?mode=grup`. Routes to /school-groups/:slug for hierarchy
 * entries, otherwise /brands/:id for legacy brand rows. Deliberately smaller than the
 * unit-card so the density signals "container of many units". */
function GroupExplorationCard({ b, i }) {
  const { school_reviews_enabled: reviewsOn } = useFlags();
  const to = b.kind === "group" ? `/grup/${b.slug}` : `/brands/${b.brand_id}`;
  return (
    <Link
      to={to}
      data-testid={`group-card-${i}`}
      className="group relative flex flex-col p-6 rounded-2xl bg-white border border-[#EAE7E0] hover:border-[#E07A5F] hover:-translate-y-1 transition-all duration-300 overflow-hidden"
    >
      <div className="absolute -top-8 -right-8 w-24 h-24 rounded-full bg-[#E07A5F]/8 group-hover:bg-[#E07A5F]/15 transition-colors" />
      <div className="relative">
        {b.logo_url ? (
          <img src={b.logo_url} alt={b.sample_name} className="w-11 h-11 rounded-xl object-contain bg-[#F3F1ED] p-1.5" />
        ) : (
          <div className="w-11 h-11 rounded-xl bg-[#F3F1ED] flex items-center justify-center text-[#1E3A8A] group-hover:bg-[#EFF6FF] transition-colors">
            {b.kind === "group" ? <Users size={20} /> : <Building2 size={20} />}
          </div>
        )}
        {b.kind === "group" && (
          <div className="mt-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FDF2EF] text-[#D2673E] text-[10px] uppercase tracking-widest font-semibold">Yayasan</div>
        )}
        <div className="mt-1.5 font-serif-display text-xl text-[#1F2937] leading-snug tracking-tight" data-testid={`group-name-${i}`}>
          {b.sample_name}
        </div>
        <div className="mt-4 flex items-center flex-wrap gap-2 text-xs text-[#4B5563]">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#EFF6FF] text-[#1E3A8A] font-semibold" data-testid={`group-branches-${i}`}>
            {b.branches} cabang{typeof b.unit_count === "number" && b.unit_count > 0 ? ` · ${b.unit_count} unit` : ""}
          </span>
          {reviewsOn && (b.review_count || 0) > 0 && (
            <span className="inline-flex items-center gap-1 text-[#4B5563]" data-testid={`group-rating-${i}`}>
              <Star size={10} className="text-[#E4B24C] fill-[#E4B24C]" />
              <strong className="text-[#1F2937]">{(b.avg_rating || 0).toFixed(1)}</strong>
              <span className="text-[10px] text-[#4B5563]">· {b.review_count} review di semua cabang</span>
            </span>
          )}
          {b.cities && b.cities.length > 0 && (
            <span className="inline-flex items-center gap-1 text-[#4B5563]">
              <MapPin size={10} /> {b.cities.slice(0, 2).join(", ")}
              {b.cities.length > 2 && ` +${b.cities.length - 2}`}
            </span>
          )}
        </div>
        {/* Jenjang availability chips — muted for levels the group doesn't cover. */}
        {b.kind === "group" && (
          <div className="mt-3 flex items-center gap-1 flex-wrap" data-testid={`group-levels-${i}`}>
            {["TK", "SD", "SMP", "SMA"].map((lvl) => {
              const has = (b.available_levels || []).includes(lvl);
              return (
                <span
                  key={lvl}
                  data-active={has}
                  className={"inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold tracking-wide " +
                    (has ? "bg-[#FDF2EF] text-[#D2673E] border border-[#F5D3C6]" : "bg-[#F3F1ED] text-[#B8BAC0] border border-[#EAE7E0]")}
                >
                  {lvl}
                </span>
              );
            })}
          </div>
        )}
        <div className="mt-5 inline-flex items-center gap-1.5 text-sm text-[#1E3A8A] font-medium group-hover:text-[#E07A5F] transition-colors">
          Lihat cabang <ArrowRight size={13} className="group-hover:translate-x-1 transition-transform" />
        </div>
      </div>
    </Link>
  );
}
